import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { listPlans, selectPlan } from "@/lib/subscriptionApi";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/contexts/AuthContext";
import { Check, ArrowLeft, Sparkles, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

const formatINR = (v) =>
  v === 0 ? "Free" : "₹" + v.toLocaleString("en-IN");

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);
  const { data: sub, refresh } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    listPlans()
      .then((r) => setPlans(r.plans || []))
      .catch((e) => toast.error("Could not load plans", { description: e?.message }))
      .finally(() => setLoading(false));
  }, []);

  const currentKey = sub?.subscription?.plan;

  const yearlyMonthlyDelta = useMemo(() => {
    const m = plans.find((p) => p.key === "pro_monthly");
    const y = plans.find((p) => p.key === "pro_yearly");
    if (!m || !y) return null;
    return m.price_inr * 12 - y.price_inr;
  }, [plans]);

  const onSelect = async (key) => {
    if (!user) {
      // Public visitor — bounce to login, then back here.
      toast.info("Sign in to choose a plan");
      navigate("/login");
      return;
    }
    if (key === currentKey) {
      toast.info("You're already on this plan");
      return;
    }
    setSelecting(key);
    try {
      await selectPlan(key);
      await refresh();
      toast.success(`Switched to ${plans.find((p) => p.key === key)?.name}`, {
        description: "This is a test-mode switch — payments arrive in Phase 2.",
      });
    } catch (e) {
      toast.error("Could not switch plan", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSelecting(null);
    }
  };

  const body = (
    <main className="max-w-7xl mx-auto px-6 md:px-8 py-10 md:py-14">
      {user ? (
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[12px] text-[#737373] hover:text-white transition-colors mb-6 font-mono-data tracking-wider uppercase"
          data-testid="pricing-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> Dashboard
        </Link>
      ) : null}

      <div className="mb-10">
        <div className="kpi-label mb-3">/ PRICING · INR · TEST MODE</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-white max-w-3xl leading-[1.05]">
            Unlock the full <span className="text-[#0A84FF]">RADAR</span> engine
          </h1>
          <p className="text-[#A3A3A3] mt-4 max-w-2xl text-[15px] leading-relaxed">
            Run more accounts, enhance with your own primary data without limits, and unlock
            the CRM Elite pipeline workspace for your team.
          </p>
          {yearlyMonthlyDelta != null && (
            <div className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono-data tracking-wider uppercase px-2 py-1 rounded-sm border border-[#32D74B]/40 bg-[#32D74B]/10 text-[#32D74B]">
              <Sparkles className="w-3 h-3"/>
              Save ₹{yearlyMonthlyDelta.toLocaleString("en-IN")} a year on Pro Yearly
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#737373]" data-testid="pricing-loading">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading plans…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pricing-grid">
            {plans.map((p) => {
              const current = p.key === currentKey;
              return (
                <div
                  key={p.key}
                  className={`flex flex-col gap-4 rounded-sm border p-6 transition-colors ${
                    p.popular
                      ? "border-[#0A84FF] bg-[#0A84FF]/5"
                      : current
                      ? "border-[#32D74B]/60 bg-[#0A0A0A]"
                      : "border-[#262626] bg-[#141414]"
                  }`}
                  data-testid={`plan-card-${p.key}`}
                >
                  <div className="flex items-start justify-between gap-2 min-h-[36px]">
                    <div>
                      <div className="font-display text-lg text-white tracking-tight">{p.name}</div>
                      <div className="kpi-label">{p.tagline}</div>
                    </div>
                    {p.popular && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono-data tracking-wider uppercase px-1.5 py-0.5 rounded-sm border border-[#0A84FF]/40 bg-[#0A84FF]/10 text-[#0A84FF]">
                        <Star className="w-2.5 h-2.5"/> Popular
                      </span>
                    )}
                    {current && (
                      <span className="text-[9px] font-mono-data tracking-wider uppercase px-1.5 py-0.5 rounded-sm border border-[#32D74B]/40 bg-[#32D74B]/10 text-[#32D74B]">
                        Current
                      </span>
                    )}
                  </div>

                  <div>
                    <div className="font-mono-data text-3xl text-white tracking-tight">
                      {formatINR(p.price_inr)}
                    </div>
                    <div className="kpi-label mt-1">/ {p.interval_label.toUpperCase()}</div>
                    {p.savings_label && (
                      <div className="text-[11px] text-[#32D74B] mt-1.5 font-mono-data tracking-wider uppercase">
                        {p.savings_label}
                      </div>
                    )}
                  </div>

                  <ul className="space-y-2 text-sm text-[#E5E5E5] leading-relaxed flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex gap-2">
                        <Check className="w-4 h-4 text-[#32D74B] shrink-0 mt-0.5"/>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {p.limitations && p.limitations.length > 0 && (
                    <ul className="space-y-1 text-[11px] text-[#737373] leading-relaxed">
                      {p.limitations.map((l) => (
                        <li key={l} className="flex gap-2">
                          <span className="text-[#525252] shrink-0">·</span>
                          <span>{l}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button
                    type="button"
                    onClick={() => onSelect(p.key)}
                    disabled={current || selecting === p.key}
                    data-testid={`plan-cta-${p.key}`}
                    className={`h-10 mt-2 rounded-sm font-medium tracking-tight transition-colors ${
                      current
                        ? "bg-[#1F1F1F] text-[#737373] cursor-default"
                        : p.popular
                        ? "bg-[#0A84FF] hover:bg-[#409CFF] text-white"
                        : "bg-white hover:bg-[#F5F5F5] text-[#0A0A0A]"
                    }`}
                  >
                    {selecting === p.key ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin"/>
                        <span>Switching…</span>
                      </>
                    ) : current ? (
                      <span>Current plan</span>
                    ) : p.price_inr === 0 ? (
                      <span>Switch to Free</span>
                    ) : (
                      <span>Choose {p.name}</span>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 max-w-2xl text-xs text-[#525252] leading-relaxed">
          <div className="kpi-label mb-2">/ ABOUT THIS PAGE</div>
          Phase 1 is a feature-gating preview. Plan switches above currently bypass payment so
          the team can validate the experience. Razorpay (test mode) will be wired in Phase 2,
          at which point this page will route through a payment flow.
        </div>
      </main>
  );

  if (user) {
    return (
      <div className="min-h-screen radar-grid-bg">
        <AppHeader />
        {body}
        <Footer compact />
      </div>
    );
  }
  return <PublicLayout>{body}</PublicLayout>;
}
